import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getApi } from "../app_config";
import { ClientAPI } from "../client/api/types";
import { queryClient } from "../state/queryClient";

import { syncLogs } from "./replicationControl";
import { useFetchEngineStatus } from "./useFetchEngineStatus";

const logsSyncKey = ["log_data", "sync"] as const;

const kRefreshEvent = "refresh-evals";
const kClientEventsIntervalMs = 5_000;
// Every Nth tick re-syncs the listing even without a refresh event, so
// changes the host doesn't announce (e.g. files landing in the dir) are
// still picked up.
const kPeriodicRefreshTicks = 10;

const clientEventsKey = (logDir: string) =>
  ["log_data", "client-events", logDir] as const;

/**
 * One client-events poll tick (the queryFn; exported for tests): ask the host
 * for events and, on a `refresh-evals` event or every
 * `kPeriodicRefreshTicks`th tick, invalidate the dir's listing-sync queries —
 * refetching whichever panel is subscribed (the tick only runs while a
 * `useLogsSync` subscriber is mounted, so the refetch is guaranteed; the
 * query, not a direct call, owns coalescing). The query data is the tick
 * counter.
 */
export const clientEventsTick = async (
  api: ClientAPI,
  logDir: string
): Promise<number> => {
  const events = await api.client_events();
  const tick =
    (queryClient.getQueryData<number>(clientEventsKey(logDir)) ?? 0) + 1;
  if (events.includes(kRefreshEvent) || tick % kPeriodicRefreshTicks === 0) {
    await queryClient.invalidateQueries({ queryKey: [...logsSyncKey, logDir] });
  }
  return tick;
};

export interface ListingStatus {
  /** A listing sync round-trip is in flight — the signal for prominent
   *  chrome like the navbar activity bar. Deliberately excludes engine
   *  background fetching: a running eval's steady re-fetches or a large
   *  dir's backfill would otherwise animate the bar near-continuously. */
  loading: boolean;
  /** The subsystem is bringing the listing up to date: the sync query is
   *  fetching for the first time or the engine is fetching items in the
   *  background. For subordinate indications (footer "Syncing data" text,
   *  empty-grid overlays). */
  busy: boolean;
  /** The listing sync failed and no sync of this log dir has succeeded this
   *  session, so there is no current listing to show. */
  error: Error | undefined;
  /** The listing sync failed after an earlier sync of this log dir succeeded
   *  (in any folder: a sync always covers the whole dir). The listing is
   *  still valid, just possibly stale; the next poll-driven re-sync retries
   *  and clears this on success. */
  refreshError: Error | undefined;
}

/**
 * Sync the log listing for a mounted panel and report its status (nothing
 * sets a busy flag imperatively). The listing data itself flows through the
 * logsContent collections via the engine's sink — subscribing triggers
 * discovery; the returned status is the one loading/busy/error signal for
 * listing surfaces. Keyed by the panel's path scope so navigating between
 * folders re-syncs. No-ops in single-file mode.
 *
 * Subscribing also keeps the listing *fresh*: a client-events poll (shared
 * across subscribers via its `logDir` key) re-syncs on host `refresh-evals`
 * events and periodically. Poll lifetime is subscriber lifetime — no
 * imperative start/stop.
 */
export const useLogsSync = (logDir: string, scope: string): ListingStatus => {
  // Deliberate exception to the useAsyncDataFromQuery convention — a
  // side-effect-only query whose result nobody consumes.
  useQuery({
    queryKey: clientEventsKey(logDir),
    queryFn: () => clientEventsTick(getApi(), logDir),
    // A tick must never park the poll: an errored query keeps refetching on
    // the interval (retries are skipped — the next tick IS the retry), so
    // listing freshness survives a transient outage of any length.
    retry: false,
    refetchInterval: kClientEventsIntervalMs,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const sync = useQuery({
    queryKey: [...logsSyncKey, logDir, scope],
    queryFn: () => syncLogs(logDir),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const { syncing, lastSyncedAt } = useFetchEngineStatus();
  const { isFetching, isPending, error } = sync;
  const synced = lastSyncedAt !== undefined;
  return useMemo(
    () => ({
      loading: isFetching,
      busy: isPending || syncing,
      error: synced ? undefined : (error ?? undefined),
      refreshError: synced ? (error ?? undefined) : undefined,
    }),
    [isFetching, isPending, error, syncing, synced]
  );
};

/**
 * Request listing freshness (external event / user refresh): invalidate the
 * listing-sync queries, re-running `syncLogs` for whichever panels are
 * subscribed. With no subscriber mounted nothing refetches — freshness is
 * subscriber-driven. Fire-and-forget: completion is observed through
 * `ListingStatus.busy`, never awaited.
 */
export const invalidateLogListing = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  queryClient.invalidateQueries({ queryKey: logsSyncKey });
};
